import React, { useEffect, useState, MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, Xmark } from 'iconoir-react';

type MobileNavItem = {
  label: string;
  href: string;
};

type MobileNavProps = {
  items?: MobileNavItem[];
  isScrolled?: boolean;
  onNavigate?: (href: string) => void;
  isInDarkSection?: boolean;
};

const DEFAULT_ITEMS: MobileNavItem[] = [
  { label: 'Home', href: '#home' },
  { label: 'About', href: '#about' },
  { label: 'Experience', href: '#experience' },
  { label: 'Projects', href: '#projects' },
  { label: 'Skills', href: '#skills' },
  { label: 'Contact', href: '#contact' },
];

const MobileNav: React.FC<MobileNavProps> = ({
  items = DEFAULT_ITEMS,
  isScrolled = false,
  onNavigate,
  isInDarkSection = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    const html = document.documentElement;
    const previousOverflow = html.style.overflow;

    if (isOpen) {
      html.style.overflow = 'hidden';
    }

    return () => {
      html.style.overflow = previousOverflow;
    };
  }, [isOpen, isMounted]);

  const handleClick = (href: string) => {
    setIsOpen(false);
    setTimeout(() => {
      if (onNavigate) {
        onNavigate(href);
        return;
      }

      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 120);
  };

  return (
    <>
      {/* Mobile menu button - only visible on mobile */}
      <div className="fixed inset-x-0 top-0 z-[160] flex items-center justify-end h-16 px-6 md:hidden">
        <button
          onClick={() => setIsOpen((open) => !open)}
          className={`p-2 transition-colors ${
            isInDarkSection
              ? 'text-white hover:text-matcha-400'
              : isScrolled
                ? 'text-gray-900 hover:text-matcha-600'
                : 'text-gray-600 hover:text-matcha-500'
          }`}
          aria-label="Toggle menu"
        >
          {isOpen ? (
            <Xmark width={24} height={24} />
          ) : (
            <Menu width={24} height={24} />
          )}
        </button>
      </div>

      {isMounted &&
        createPortal(
          <AnimatePresence>
            {isOpen && (
              <>
                {/* Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setIsOpen(false)}
                  className="fixed inset-0 z-[140] bg-black/60 backdrop-blur-sm md:hidden"
                />

                {/* Drawer - clean design */}
                <motion.div
                  initial={{ x: '100%' }}
                  animate={{ x: 0 }}
                  exit={{ x: '100%' }}
                  transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                  className="fixed top-0 right-0 z-[150] h-full w-72 overflow-y-auto bg-white shadow-2xl md:hidden"
                >
                  <div className="relative pt-20 px-6 pb-10">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-8 pb-4 border-b border-gray-100">
                      <span className="font-mono text-sm text-matcha-600">&gt;</span>
                      <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
                    </div>

                    {/* Navigation items */}
                    <nav className="space-y-1">
                      {items.map((item, index) => (
                        <motion.a
                          key={item.href}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          href={item.href}
                          onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                            event.preventDefault();
                            handleClick(item.href);
                          }}
                          className="group flex items-center gap-3 rounded-lg px-4 py-3 text-gray-600 transition-all hover:bg-gray-50 hover:text-matcha-600"
                        >
                          <div className="w-1.5 h-1.5 bg-matcha-400 rounded-full opacity-50 group-hover:opacity-100 transition-opacity" />
                          <span className="font-medium">{item.label}</span>
                        </motion.a>
                      ))}
                    </nav>

                    {/* Social links */}
                    <div className="mt-10 pt-6 border-t border-gray-100">
                      <p className="mb-4 font-mono text-xs text-gray-400 uppercase tracking-wider">
                        Connect
                      </p>
                      <div className="flex gap-3">
                        <a
                          href="https://linkedin.com/in/anthony-abid"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center text-gray-500 transition-all hover:bg-matcha-100 hover:text-matcha-600 border border-gray-100"
                        >
                          <span className="sr-only">LinkedIn</span>
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                          </svg>
                        </a>
                        <a
                          href="mailto:abid@certfid.io"
                          className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center text-gray-500 transition-all hover:bg-matcha-100 hover:text-matcha-600 border border-gray-100"
                        >
                          <span className="sr-only">Email</span>
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                          </svg>
                        </a>
                        <a
                          href="https://github.com/AnthonyA671"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center text-gray-500 transition-all hover:bg-matcha-100 hover:text-matcha-600 border border-gray-100"
                        >
                          <span className="sr-only">GitHub</span>
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </a>
                      </div>
                    </div>

                    {/* Signature */}
                    <div className="mt-8 text-center">
                      <p className="text-sm text-gray-400 italic" style={{ fontFamily: 'cursive' }}>
                        — Anthony Abid
                      </p>
                      <p className="font-mono text-xs text-gray-300 mt-1">
                        2025
                      </p>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>,
          document.body
        )}
    </>
  );
};

export default MobileNav;
